import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ShoppingBag, Heart, Star, ChevronLeft, Share2, Check, Leaf } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import ProductCard from "@/components/ProductCard";
import DeliveryInfo from "@/components/DeliveryInfo";

import { API_BASE as API } from "@/config/api";
import { getWhatsAppUrl } from "@/config/contact";

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [customNote, setCustomNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [wishlisted, setWishlisted] = useState(false);
  const [reviewText, setReviewText] = useState("");
  const [reviewRating, setReviewRating] = useState(5);
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/products/${id}`)
      .then(({ data }) => {
        setProduct(data);
        // Load related products
        axios.get(`${API}/products?category=${encodeURIComponent(data.category)}&limit=4`).then(r => {
          setRelated((r.data.products || []).filter(p => p.id !== id));
        });
        // Load reviews
        axios.get(`${API}/reviews/${id}`).then(r => setReviews(r.data || []));
      })
      .catch(() => navigate("/shop"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleAddToCart = async () => {
    if (!user) { navigate("/login"); return; }
    setAdding(true);
    try {
      await addToCart(product.id, quantity, customNote);
      setAdded(true);
      setTimeout(() => setAdded(false), 2000);
    } catch (e) { console.error(e); }
    finally { setAdding(false); }
  };

  const handleBuyNow = async () => {
    if (!user) { navigate("/login"); return; }
    setAdding(true);
    try {
      await addToCart(product.id, quantity, customNote);
      navigate("/checkout");
    } catch (e) { console.error(e); }
    finally { setAdding(false); }
  };

  const handleWishlist = async () => {
    if (!user) { navigate("/login"); return; }
    try {
      if (wishlisted) {
        await axios.delete(`${API}/wishlist/${id}`, { withCredentials: true });
      } else {
        await axios.post(`${API}/wishlist/${id}`, {}, { withCredentials: true });
      }
      setWishlisted(!wishlisted);
    } catch (e) { console.error(e); }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login"); return; }
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/reviews`, { product_id: id, rating: reviewRating, comment: reviewText }, { withCredentials: true });
      const { data } = await axios.get(`${API}/reviews/${id}`);
      setReviews(data || []);
      setReviewText(""); setReviewRating(5);
    } catch (e) { console.error(e); }
    finally { setSubmittingReview(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-petal-beige flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-petal-olive border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!product) return null;

  const discount = product.original_price ? Math.round(((product.original_price - product.price) / product.original_price) * 100) : 0;

  return (
    <div className="min-h-screen bg-petal-beige" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
        <div className="flex items-center gap-2 font-outfit text-xs text-petal-muted">
          <Link to="/" className="hover:text-petal-olive transition-colors">Home</Link>
          <span>/</span>
          <Link to="/shop" className="hover:text-petal-olive transition-colors">Shop</Link>
          <span>/</span>
          <span className="text-petal-olive-text">{product.name}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20">
          {/* Images */}
          <div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative overflow-hidden aspect-square bg-white border border-petal-border mb-4">
              <img src={product.images?.[selectedImage] || "https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=600&q=80"}
                alt={product.name} className="w-full h-full object-cover" data-testid="product-main-image" />
              {discount > 0 && (
                <div className="absolute top-4 left-4 bg-red-400 text-white text-xs px-3 py-1 font-outfit">{discount}% OFF</div>
              )}
            </motion.div>
            {product.images?.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {product.images.map((img, i) => (
                  <button key={i} onClick={() => setSelectedImage(i)}
                    className={`aspect-square overflow-hidden border-2 transition-colors ${i === selectedImage ? "border-petal-olive" : "border-petal-border"}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="space-y-6">
            <div>
              <p className="font-outfit text-xs uppercase tracking-[0.2em] text-petal-muted mb-2">{product.category}</p>
              <h1 className="font-playfair text-3xl sm:text-4xl text-petal-olive-text mb-4" data-testid="product-name">{product.name}</h1>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`w-4 h-4 ${i < Math.floor(product.rating || 0) ? "fill-petal-gold text-petal-gold" : "text-petal-border"}`} />
                  ))}
                </div>
                <span className="font-outfit text-sm text-petal-muted">({product.review_count} reviews)</span>
              </div>
              <div className="flex items-baseline gap-3">
                <span className="font-playfair text-4xl text-petal-olive-text font-semibold" data-testid="product-price">₹{product.price.toLocaleString()}</span>
                {product.original_price && (
                  <span className="font-outfit text-lg text-petal-muted line-through">₹{product.original_price.toLocaleString()}</span>
                )}
              </div>
            </div>

            {/* Description */}
            <p className="font-outfit text-sm text-petal-muted leading-relaxed border-t border-petal-border pt-6">{product.description}</p>

            {/* Features */}
            <div className="grid grid-cols-2 gap-3">
              {[["Handmade","With love"], ["Premium","Quality"]].map(([t, d]) => (
                <div key={t} className="border border-petal-border p-3 text-center">
                  <Leaf className="w-4 h-4 text-petal-gold mx-auto mb-1" />
                  <p className="font-outfit text-xs font-semibold text-petal-olive-text">{t}</p>
                  <p className="font-outfit text-xs text-petal-muted">{d}</p>
                </div>
              ))}
            </div>

            <DeliveryInfo />

            {/* Custom Note */}
            {product.customizable && (
              <div className="border border-petal-border p-4 bg-petal-beige-alt">
                <p className="font-outfit text-xs uppercase tracking-widest text-petal-olive mb-3">Personalize It</p>
                <textarea value={customNote} onChange={e => setCustomNote(e.target.value)}
                  placeholder="Add a personal message, name, or special request..."
                  rows={3} data-testid="custom-note-input"
                  className="w-full px-3 py-2 border border-petal-border bg-white font-outfit text-sm resize-none focus:outline-none focus:border-petal-olive" />
              </div>
            )}

            {/* Quantity */}
            <div className="flex items-center gap-4">
              <p className="font-outfit text-sm uppercase tracking-widest text-petal-olive-text">Qty:</p>
              <div className="flex items-center border border-petal-border">
                <button onClick={() => setQuantity(q => Math.max(1, q - 1))} data-testid="qty-decrease"
                  className="w-10 h-10 flex items-center justify-center hover:bg-petal-beige-alt transition-colors text-lg">-</button>
                <span className="w-12 text-center font-outfit text-sm font-medium" data-testid="qty-value">{quantity}</span>
                <button onClick={() => setQuantity(q => Math.min(product.stock, q + 1))} data-testid="qty-increase"
                  className="w-10 h-10 flex items-center justify-center hover:bg-petal-beige-alt transition-colors text-lg">+</button>
              </div>
              <span className="font-outfit text-xs text-petal-muted">{product.stock} in stock</span>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button onClick={handleAddToCart} disabled={adding} data-testid="add-to-cart-btn"
                className={`flex-1 flex items-center justify-center gap-2 py-4 font-outfit text-sm uppercase tracking-widest transition-colors ${added ? "bg-green-500 text-white" : "bg-petal-olive text-white hover:bg-petal-olive-dark"}`}>
                {added ? <><Check className="w-4 h-4" /> Added!</> : adding ? "Adding..." : <><ShoppingBag className="w-4 h-4" /> Add to Cart</>}
              </button>
              <button onClick={handleBuyNow} data-testid="buy-now-btn"
                className="flex-1 border border-petal-gold text-petal-gold py-4 font-outfit text-sm uppercase tracking-widest hover:bg-petal-gold hover:text-white transition-colors">
                Buy Now
              </button>
              <button onClick={handleWishlist} data-testid="wishlist-btn"
                className={`w-14 border flex items-center justify-center transition-colors ${wishlisted ? "border-red-300 bg-red-50 text-red-400" : "border-petal-border text-petal-muted hover:border-red-300 hover:text-red-400"}`}>
                <Heart className={`w-5 h-5 ${wishlisted ? "fill-red-400" : ""}`} />
              </button>
            </div>

            {/* WhatsApp */}
            <a href={getWhatsAppUrl(`Hi! I'm interested in ${product.name} (₹${product.price}). Custom note: ${customNote || "none"}`)}
              target="_blank" rel="noopener noreferrer" data-testid="whatsapp-product-btn"
              className="flex items-center justify-center gap-2 py-3 border border-[#25D366] text-[#25D366] font-outfit text-sm uppercase tracking-widest hover:bg-[#25D366] hover:text-white transition-colors">
              Order via WhatsApp
            </a>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-20 border-t border-petal-border pt-16">
          <h2 className="section-title mb-8" data-testid="reviews-section">Customer Reviews</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Write Review */}
            {user && (
              <div className="bg-white border border-petal-border p-6">
                <h3 className="font-playfair text-xl text-petal-olive-text mb-4">Write a Review</h3>
                <form onSubmit={handleReviewSubmit} className="space-y-4">
                  <div className="flex gap-2">
                    {[1,2,3,4,5].map(s => (
                      <button key={s} type="button" onClick={() => setReviewRating(s)} data-testid={`review-star-${s}`}>
                        <Star className={`w-6 h-6 ${s <= reviewRating ? "fill-petal-gold text-petal-gold" : "text-petal-border"}`} />
                      </button>
                    ))}
                  </div>
                  <textarea value={reviewText} onChange={e => setReviewText(e.target.value)} required
                    placeholder="Share your experience..." rows={4} data-testid="review-textarea"
                    className="w-full px-3 py-2 border border-petal-border bg-petal-beige font-outfit text-sm resize-none focus:outline-none focus:border-petal-olive" />
                  <button type="submit" disabled={submittingReview || !reviewText.trim()} data-testid="submit-review-btn"
                    className="btn-primary text-xs py-2.5 px-6 disabled:opacity-50">
                    {submittingReview ? "Submitting..." : "Submit Review"}
                  </button>
                </form>
              </div>
            )}
            {/* Reviews list */}
            <div className="space-y-4">
              {reviews.length === 0 ? (
                <p className="font-outfit text-sm text-petal-muted">No reviews yet. Be the first to review!</p>
              ) : reviews.slice(0, 5).map(r => (
                <div key={r.id} className="bg-white border border-petal-border p-4">
                  <div className="flex justify-between mb-2">
                    <p className="font-outfit text-sm font-semibold text-petal-olive-text">{r.user_name}</p>
                    <p className="font-outfit text-xs text-petal-muted">{new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex gap-1 mb-2">
                    {[...Array(5)].map((_, i) => <Star key={i} className={`w-3 h-3 ${i < r.rating ? "fill-petal-gold text-petal-gold" : "text-petal-border"}`} />)}
                  </div>
                  <p className="font-outfit text-sm text-petal-muted">{r.comment}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Related Products */}
        {related.length > 0 && (
          <div className="mt-20 border-t border-petal-border pt-16">
            <h2 className="section-title mb-8">You May Also Like</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {related.slice(0, 4).map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
